"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatDegreesMinutesSeconds = exports.convert = void 0;
/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
const thousand = 1000;
const million = 1000000;
// Distance
const mmInAnInch = 25.4;
const mmInAFoot = mmInAnInch * 12;
// Temperature
const cToFFactor = 9 / 5;
const cToFOffset = 32;
const cToKOffset = 273.15;
const fToKOffset = 459.67;
// Angle
const degInACircle = 360;
const degToRad = Math.PI / 180;
// Humidity (or density) - kg per cubic metre to pounds per cubic foot
const kgm3InLbft3 = 16.01846337396;
// Pressure
const pascalInAMillibar = 100;
const pascalInABar = 100000;
const pascalInAPsi = 6894.75728;
// Mass
const poundsInAKilogram = 2.2046226218;
// Time
const secInAMin = 60;
/** Multiply by a thousand */
const thousandTimes = (x) => x * thousand;
/** Divide by a thousand */
const thousandth = (x) => x / thousand;
// Use standard symbols as defined in index.ts
/** Returns a set of functions for converting from/to different units,
 * e.g. for 32 millimetres in inches, use `convert.mm.to.in(32)`.
 * We expect all API data to be metric.
 * Conversion from American/Imperial to metric is implemented only for distance, since only IPC distance values need to be written back to the API.
 */
exports.convert = {
    /** millimetres */
    mm: {
        type: "distance",
        to: {
            /** feet */
            ft: (x) => x / mmInAFoot,
            /** inches */
            in: (x) => x / mmInAnInch,
            /** thousandths of an inch, AKA 'mill' or 'thou' */
            min: (x) => (x / mmInAnInch) * thousand,
            /** metres */
            m: thousandth,
            /** micrometres */
            µm: thousandTimes,
        },
    },
    /** inches */
    in: {
        type: "distance",
        to: {
            /** millimetres */
            mm: (x) => x * mmInAnInch,
            /** micrometres */
            µm: (x) => x * mmInAnInch * thousand,
        },
    },
    /** degrees Celsius */
    "°C": {
        type: "temperature",
        to: {
            /** degrees Fahrenheit */
            "°F": (x) => x * cToFFactor + cToFOffset,
            /** Kelvins */
            K: (x) => x + cToKOffset,
        },
    },
    /** Kelvin */
    K: {
        type: "temperature",
        to: {
            /** degrees Fahrenheit */
            "°F": (x) => x * cToFFactor - fToKOffset,
            /** degrees Celsius */
            "°C": (x) => x - cToKOffset,
        },
    },
    /** degrees of rotation */
    "°": {
        type: "angle",
        to: {
            /** radians */
            rad: (x) => (x % degInACircle) * degToRad,
            /** full turns */
            turn: (x) => x / degInACircle,
            // This can only be a string, but the display components need numbers
            // "°′″": formatDegreesMinutesSeconds,
        },
    },
    /** Pascal */
    Pa: {
        type: "pressure",
        to: {
            /** pounds per square inch */
            psi: (x) => x / pascalInAPsi,
            /** bar (approximately atmospheric pressure) */
            bar: (x) => x / pascalInABar,
            /** millibars */
            mbar: (x) => x / pascalInAMillibar,
        },
    },
    /** millimetres per second */
    "mm/s": {
        type: "speed",
        to: {
            /** metres per second */
            "m/s": thousandth,
            /** inches per second */
            "in/s": (x) => x / mmInAnInch,
            /** feet per second */
            "ft/s": (x) => x / mmInAFoot,
        },
    },
    /** metres per second per second */
    "m/s²": {
        type: "acceleration",
        to: {
            /** millimetres per second per second */
            "mm/s²": thousandTimes,
            /** inches per second per second */
            "in/s²": (x) => (x * thousand) / mmInAnInch,
            /** feet per second per second */
            "ft/s²": (x) => (x * thousand) / mmInAFoot,
        },
    },
    // TODO: ppm may not be a valid source unit; consider removing this
    /** parts per million */
    ppm: {
        type: "percentage",
        to: {
            /** percent */
            "%": (x) => x / 10000,
            /** per mille */
            "‰": thousandth,
            /** Micromol per mol – this is a no-op conversion */
            "µmol/mol": (x) => x,
            /** mol per mol */
            "mol/mol": (x) => x / million,
        },
    },
    /** percent */
    "%": {
        type: "percentage",
        to: {
            /** percent */
            ppm: (x) => x * 10000,
            /** per mille */
            "‰": (x) => x * 10,
        },
    },
    /** mol per mol */
    "mol/mol": {
        type: "amountOfSubstanceFraction",
        to: {
            /** parts per million */
            ppm: (x) => x * million,
            /** percent */
            "%": (x) => x * 100,
            /** per mille */
            "‰": thousandTimes,
            /** micromol per mol */
            "µmol/mol": (x) => x * million,
        },
    },
    /** kilograms */
    kg: {
        type: "mass",
        to: {
            /** grams */
            g: thousandTimes,
            /** pounds */
            lb: (x) => x * poundsInAKilogram,
        },
    },
    /** kilograms per cubic metre */
    "kg/m³": {
        type: "massDensity",
        to: {
            /** pounds per cubic foot */
            "lb/ft³": (x) => x / kgm3InLbft3,
            /** grams per cubic metre */
            "g/m³": thousandTimes,
            /** grams per litre (no-op) */
            "g/L": (x) => x,
        },
    },
    /** cubic metres per second */
    "m³/s": {
        type: "volumeDividedByFlow",
        to: {
            /** cubic metres per minute */
            "m³/min": (x) => x * 60,
            /** cubic metres per hour */
            "m³/hr": (x) => x * 3600,
            /** litres per second */
            "L/s": thousandTimes,
            /** litres per minute */
            "L/min": (x) => x * 60000,
            /** litres per hour */
            "L/hr": (x) => x * 3600000,
            /** millilitres per second */
            "mL/s": (x) => x * million,
            /** millilitres per minute */
            "mL/min": (x) => x * 60000000,
        },
    },
    /** Hertz */
    Hz: {
        type: "rotationalFrequency",
        to: {
            /** cycles per second (no-op) */
            cps: (x) => x,
            /** rotations per minute */
            rpm: (x) => x * 60,
            /** kilohertz */
            kHz: (x) => x / 1000,
        },
    },
    /** Amperes */
    A: {
        type: "electricCurrent",
        to: {
            /** milliamperes */
            mA: thousandTimes,
        },
    },
    /** Volts */
    V: {
        type: "electricPotentialDifference",
        to: {
            /** millivolts */
            mV: thousandTimes,
        },
    },
    /* Time */
    /** Minutes  */
    min: {
        type: "time",
        to: {
            /** Seconds */
            sec: (x) => x * secInAMin,
        },
    },
    /** Seconds  */
    sec: {
        type: "time",
        to: {
            /** Minutes */
            min: (x) => x / secInAMin,
        },
    },
};
/** Convert decimal degrees to degrees, minutes and seconds of arc, as a string with unit symbols */
const formatDegreesMinutesSeconds = (x) => {
    return `${Math.floor(x % 360)}° ${Math.floor((x * 60) % 60)}′ ${((x * 3600) %
        60).toFixed(1)}″`;
};
exports.formatDegreesMinutesSeconds = formatDegreesMinutesSeconds;
//# sourceMappingURL=conversion.js.map